/**
 * SettingsMenu — Phase B.1a (E.21.39)
 *
 * Small dropdown surface mounted from the existing TopBar Settings cog.
 * Currently a single auto-rebuild checkbox bound to configStore; designed
 * to grow with more SPA-facing config fields without UI redesign.
 *
 * Mirrors the mind_chat_plugin/frontend-v2 RAGSourcesPanel pattern (toggle
 * UI driven by store) but uses a single PATCH /api/config endpoint instead
 * of per-source POST /sources/{id}/toggle. The backend persists the
 * override to /workspace/.vibe-config.json so it survives restart.
 *
 * Toast feedback is opt-in here and emits via useToast on changes — falls
 * back to console.error if not within a ToastProvider (defensive — the
 * component MAY be rendered in storybook / standalone tests without one).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useConfigStore } from '../stores/configStore';
import { useVoiceStore, type VoicePresetId } from '../stores/voiceStore';
import {
  CHAT_HARNESS_LABELS,
  useChatHarnessStore,
  type ChatHarnessId,
} from '../stores/chatHarnessStore';
import {
  useChatPrefsStore,
  PI_CHAT_FONT_SIZE_RANGE,
  PI_CHAT_INPUT_FONT_SIZE_RANGE,
  SPINNER_SELECTABLE,
  SPINNER_SCALE_RANGE,
  ASCII_SPINNER_SELECTABLE,
  type ThinkingLevel,
  type QueueMode,
  type SpinnerSelectable,
  type SpinnerFontId,
  type AsciiSpinnerSelectable,
} from '../stores/chatPrefsStore';
import { SPINNER_THEMES } from './layout/spinners/spinnerThemes';
import { SPINNER_FONTS } from './layout/spinners/spinnerFonts';
import { ASCII_THOUGHT_MOVIES } from './layout/spinners/asciiThoughtFrames';
import {
  useUIStore,
  INTERFACE_FONT_SIZE_RANGE,
  EDITOR_FONT_SIZE_RANGE,
} from '../stores/uiStore';
import { useTenantStore } from '../stores/tenantStore';
import { useToast } from './useToast';
import { syncHistoryToKnowledge } from '../services/historySync';
import { ProviderKeysSection } from './ProviderKeysSection';

interface SettingsMenuProps {
  open: boolean;
  onClose: () => void;
}

/**
 * SliderTicks (1.552.0) — min / default / max scale labels beneath a font-size
 * range slider so the operator can see the full span of available sizes (the
 * sliders themselves widened in 1.552.0). Decorative — the live value is shown
 * in the slider's own label; these are aria-hidden.
 */
function SliderTicks({
  range,
  testId,
}: {
  range: { min: number; max: number; default: number };
  testId: string;
}) {
  return (
    <div
      data-testid={testId}
      aria-hidden="true"
      className="flex justify-between text-[10px] leading-none font-mono text-vibe-text-secondary mt-1 px-0.5"
    >
      <span>{range.min}</span>
      <span>{range.default} (default)</span>
      <span>{range.max}</span>
    </div>
  );
}

/**
 * Section (1.553.0) — a distinct rounded-edge card around one logical group of
 * settings so the panel reads as discrete sections instead of one long, gappy
 * grid. Optional uppercase title; children own their internal layout (the Text
 * size card uses a 2-col grid, the rest stack).
 */
function Section({
  title,
  children,
  testId,
}: {
  title?: string;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className="rounded-lg border border-vibe-border bg-vibe-bg/40 p-3"
    >
      {title && (
        <div className="text-[11px] font-semibold uppercase tracking-wide text-vibe-text-secondary mb-2.5">
          {title}
        </div>
      )}
      {children}
    </div>
  );
}

/** 1.554.0 — display label for a spinner style/messages select option. */
function spinnerOptionLabel(value: SpinnerSelectable): string {
  if (value === 'rotate') return 'Rotate (cycle all)';
  return SPINNER_THEMES.find((t) => t.id === value)?.name ?? value;
}

/** 1.569.0 — display label for the in-thread ASCII animation select option. */
function asciiSpinnerOptionLabel(value: AsciiSpinnerSelectable): string {
  if (value === 'rotate') return 'Rotate (cycle all)';
  if (value === 'random') return 'Randomize';
  return ASCII_THOUGHT_MOVIES.find((m) => m.id === value)?.name ?? value;
}

export function SettingsMenu({ open, onClose }: SettingsMenuProps) {
  const autoRebuildOnSave = useConfigStore((s) => s.autoRebuildOnSave);
  const setAutoRebuildOnSave = useConfigStore((s) => s.setAutoRebuildOnSave);
  // mm_post tenant toggle (2026-07-13) — agents may post to Mattermost
  // only after the tenant user opts in. Backend enforces per call.
  const agentMmPostEnabled = useConfigStore((s) => s.agentMmPostEnabled);
  const setAgentMmPostEnabled = useConfigStore((s) => s.setAgentMmPostEnabled);
  const error = useConfigStore((s) => s.error);
  // 2026-05-07: multi-engine voice picker. Engine dropdown above Voice
  // dropdown; Voice list reloads when Engine changes. Both persist via
  // updateVoiceSettings -> setStoredSettings.
  const voiceSettings = useVoiceStore((s) => s.settings);
  const voices = useVoiceStore((s) => s.voices);
  const engines = useVoiceStore((s) => s.engines);
  const updateVoiceSettings = useVoiceStore((s) => s.updateSettings);
  const setEngine = useVoiceStore((s) => s.setEngine);
  const loadEngines = useVoiceStore((s) => s.loadEngines);
  // Plan E v1.1.0: chat harness dropdown — runtime per-tenant/user toggle
  // replaces the build-time VITE_USE_PI_CHAT flag.
  const harness = useChatHarnessStore((s) => s.harness);
  const setHarness = useChatHarnessStore((s) => s.setHarness);
  // 1.82.0: Pi chat font-size slider (12-20px, default 14). Per-tenant
  // localStorage via chatPrefsStore. ChatPanelPi.tsx applies the value as
  // a CSS variable that flows through ChatPanelPi.css to all Pi-rendered
  // descendants via the Light DOM cascade.
  const fontSize = useChatPrefsStore((s) => s.fontSize);
  const setFontSize = useChatPrefsStore((s) => s.setFontSize);
  // 1.552.0: dedicated composer input-area (+ status spinner) font size,
  // independent of the transcript size above. Sets --vibe-pi-input-font-size.
  const inputFontSize = useChatPrefsStore((s) => s.inputFontSize);
  const setInputFontSize = useChatPrefsStore((s) => s.setInputFontSize);
  // 1.88.0: global interface font size. Applied to document.documentElement
  // by App.tsx so all Tailwind text-* classes scale proportionally. Distinct
  // from the per-tenant Pi-chat fontSize above (which fine-tunes the Pi
  // chat panel relative to global via --vibe-pi-chat-font-size). Persisted
  // per-browser via uiStore's persist middleware (partialize whitelist).
  const interfaceFontSize = useUIStore((s) => s.interfaceFontSize);
  const setInterfaceFontSize = useUIStore((s) => s.setInterfaceFontSize);
  // 1.91.0: Monaco editor font size, independent from interface scale.
  // Monaco's content area is canvas-rendered and does NOT inherit
  // document.documentElement font-size, so this slider is the only
  // way to scale the code-editor text. Per-browser persistence via
  // uiStore. Range 10-22px, default 14 (matches Monaco's prior
  // hardcoded value at EditorPanel.tsx:322 pre-1.91.0).
  const editorFontSize = useUIStore((s) => s.editorFontSize);
  const setEditorFontSize = useUIStore((s) => s.setEditorFontSize);
  // 1.87.0: Pi Chat thinking level toggle. Off / Low / Medium / High.
  // chatPrefsStore.thinkingLevel is hydrated from localStorage per tenant
  // and consumed by piAgentFactory at Agent construction time so new tabs
  // pick up the user's setting. Pi-web-ui's <pi-chat-panel> ALSO renders
  // its own thinking selector inline above each chat (see AgentInterface.ts:
  // 367) which writes back to agent.state.thinkingLevel directly — that
  // path mutates the live agent without going through this store.
  const thinkingLevel = useChatPrefsStore((s) => s.thinkingLevel);
  const setThinkingLevel = useChatPrefsStore((s) => s.setThinkingLevel);
  // 1.97.0: Pi Chat queue mode toggle. followUp (default, queues for after
  // current task) vs steer (injects between assistant turns mid-task).
  // Drives ChatPanelPi's queue-row Send action when state.isStreaming=true.
  const queueMode = useChatPrefsStore((s) => s.queueMode);
  const setQueueMode = useChatPrefsStore((s) => s.setQueueMode);
  // 1.420.0: Pi chat auto-compact toggle. The flag shipped in 1.419.0 but was
  // only reachable from the ≥80% context WARNING banner; Settings makes it
  // settable any time. Per-tenant localStorage via chatPrefsStore.
  const autoCompact = useChatPrefsStore((s) => s.autoCompact);
  const setAutoCompact = useChatPrefsStore((s) => s.setAutoCompact);
  // 1.554.0: Pi status-spinner theme picker — animated style, message pool,
  // and size multiplier (1× = original spinner size, 2×/3× scale up).
  // Per-tenant localStorage via chatPrefsStore. Default cosmos/cosmos/1× keeps
  // the original Cosmos theme + phrases at the original size, so switching
  // theme or growing the spinner is opt-in.
  const spinnerVariant = useChatPrefsStore((s) => s.spinnerVariant);
  const setSpinnerVariant = useChatPrefsStore((s) => s.setSpinnerVariant);
  const spinnerMessages = useChatPrefsStore((s) => s.spinnerMessages);
  const setSpinnerMessages = useChatPrefsStore((s) => s.setSpinnerMessages);
  const spinnerScale = useChatPrefsStore((s) => s.spinnerScale);
  const setSpinnerScale = useChatPrefsStore((s) => s.setSpinnerScale);
  const spinnerFont = useChatPrefsStore((s) => s.spinnerFont);
  const setSpinnerFont = useChatPrefsStore((s) => s.setSpinnerFont);
  // 1.569.0: in-thread ASCII "thought" spinner — which Braille animation plays
  // where Pi's next message appears. Default 'rotate' cycles the set per turn.
  const asciiSpinner = useChatPrefsStore((s) => s.asciiSpinner);
  const setAsciiSpinner = useChatPrefsStore((s) => s.setAsciiSpinner);
  const tenantId = useTenantStore((s) => s.tenantId);
  // Phase 4 (2026-07-13): one-shot browser-history push to the
  // knowledge backend (Pi IndexedDB + voice localStorage).
  const [historySyncing, setHistorySyncing] = useState(false);
  const toast = useToast();

  // Load engines list on first mount (lazy — only when settings menu opens).
  useEffect(() => {
    if (open && engines.length === 0) {
      void loadEngines();
    }
  }, [open, engines.length, loadEngines]);

  // Track the previous error value so we only toast on a NEW error, not
  // on every render. Mirror Phase B's useAutoRebuildOnSave pattern.
  const prevErrorRef = useRef<Error | null>(null);
  useEffect(() => {
    if (error && error !== prevErrorRef.current) {
      toast.error(`Config update failed: ${error.message}`, 4000);
    }
    prevErrorRef.current = error;
  }, [error, toast]);

  if (!open) return null;

  const isLoadingConfig = autoRebuildOnSave === null;

  const handleToggle = async () => {
    // Flip — read latest value at click time, not from props closure.
    const next = !(autoRebuildOnSave === true);
    await setAutoRebuildOnSave(next);
    // Read AFTER the action resolves — server response is source of truth.
    const finalValue = useConfigStore.getState().autoRebuildOnSave;
    const finalError = useConfigStore.getState().error;
    if (!finalError && finalValue === next) {
      toast.success(
        next ? 'Auto-rebuild enabled' : 'Auto-rebuild disabled',
        2000,
      );
    }
    // The error path is handled by the useEffect above.
  };

  const handleMmPostToggle = async () => {
    const next = !(agentMmPostEnabled === true);
    await setAgentMmPostEnabled(next);
    const finalValue = useConfigStore.getState().agentMmPostEnabled;
    const finalError = useConfigStore.getState().error;
    if (!finalError && finalValue === next) {
      toast.success(
        next
          ? 'Agents may now post to Mattermost'
          : 'Agent Mattermost posting disabled',
        2000,
      );
    }
  };

  return (
    <>
      <div
        data-testid="settings-menu-backdrop"
        className="fixed inset-0 z-[10006]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        data-testid="settings-menu"
        className="absolute right-0 top-full mt-1 w-[640px] max-h-[80vh] overflow-y-auto z-[10007] bg-vibe-bg-secondary border border-vibe-border rounded shadow-lg"
      >
      <div className="flex items-center justify-between px-3 py-2 border-b border-vibe-border">
        <span className="text-sm font-medium text-vibe-text">Settings</span>
        <button
          onClick={onClose}
          className="text-xs text-vibe-text-secondary hover:text-vibe-text"
          aria-label="Close settings menu"
        >
          ✕
        </button>
      </div>
      <div className="px-3 py-3 space-y-3">
        {/* 1.553.0 — Text size: all four font sliders grouped in one card
            (2-col) so they're no longer scattered across the panel. */}
        <Section title="Text size" testId="settings-menu-section-text-size">
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <div>
          <label className="block text-sm text-vibe-text mb-1.5">
            Interface font size: <span className="font-mono">{interfaceFontSize}px</span>
          </label>
          <input
            type="range"
            min={INTERFACE_FONT_SIZE_RANGE.min}
            max={INTERFACE_FONT_SIZE_RANGE.max}
            step={1}
            value={interfaceFontSize}
            onChange={(e) => setInterfaceFontSize(Number(e.target.value))}
            data-testid="settings-menu-interface-font-size"
            className="block w-full"
          />
          <SliderTicks
            range={INTERFACE_FONT_SIZE_RANGE}
            testId="settings-menu-interface-font-size-ticks"
          />
          <div className="text-xs text-vibe-text-secondary mt-1">
            Scales the entire SPA chrome ({INTERFACE_FONT_SIZE_RANGE.min}–{INTERFACE_FONT_SIZE_RANGE.max}px,
            default {INTERFACE_FONT_SIZE_RANGE.default}). Applies to top bar,
            sidebar, panels, and chat by changing the document root font size.
            Monaco editor has its own font setting and is not affected.
            Persists per browser via localStorage.
          </div>
        </div>

        <div>
          <label className="block text-sm text-vibe-text mb-1.5">
            Editor font size: <span className="font-mono">{editorFontSize}px</span>
          </label>
          <input
            type="range"
            min={EDITOR_FONT_SIZE_RANGE.min}
            max={EDITOR_FONT_SIZE_RANGE.max}
            step={1}
            value={editorFontSize}
            onChange={(e) => setEditorFontSize(Number(e.target.value))}
            data-testid="settings-menu-editor-font-size"
            className="block w-full"
          />
          <SliderTicks
            range={EDITOR_FONT_SIZE_RANGE}
            testId="settings-menu-editor-font-size-ticks"
          />
          <div className="text-xs text-vibe-text-secondary mt-1">
            Scales the Monaco code editor only ({EDITOR_FONT_SIZE_RANGE.min}–{EDITOR_FONT_SIZE_RANGE.max}px,
            default {EDITOR_FONT_SIZE_RANGE.default}). Independent of the
            interface size. Persists per browser via localStorage.
          </div>
        </div>

        <div>
          <label className="block text-sm text-vibe-text mb-1.5">
            Pi chat font size: <span className="font-mono">{fontSize}px</span>
          </label>
          <input
            type="range"
            min={PI_CHAT_FONT_SIZE_RANGE.min}
            max={PI_CHAT_FONT_SIZE_RANGE.max}
            step={1}
            value={fontSize}
            onChange={(e) => {
              if (!tenantId) return;
              setFontSize(tenantId, Number(e.target.value));
            }}
            data-testid="settings-menu-pi-chat-font-size"
            disabled={!tenantId}
            className="block w-full disabled:opacity-50"
          />
          <SliderTicks
            range={PI_CHAT_FONT_SIZE_RANGE}
            testId="settings-menu-pi-chat-font-size-ticks"
          />
          <div className="text-xs text-vibe-text-secondary mt-1">
            Font size of the Pi chat transcript ({PI_CHAT_FONT_SIZE_RANGE.min}–{PI_CHAT_FONT_SIZE_RANGE.max}px).
            Per tenant per browser. Default {PI_CHAT_FONT_SIZE_RANGE.default}px.
          </div>
        </div>

        <div>
          <label className="block text-sm text-vibe-text mb-1.5">
            Pi chat input size: <span className="font-mono">{inputFontSize}px</span>
          </label>
          <input
            type="range"
            min={PI_CHAT_INPUT_FONT_SIZE_RANGE.min}
            max={PI_CHAT_INPUT_FONT_SIZE_RANGE.max}
            step={1}
            value={inputFontSize}
            onChange={(e) => {
              if (!tenantId) return;
              setInputFontSize(tenantId, Number(e.target.value));
            }}
            data-testid="settings-menu-pi-chat-input-font-size"
            disabled={!tenantId}
            className="block w-full disabled:opacity-50"
          />
          <SliderTicks
            range={PI_CHAT_INPUT_FONT_SIZE_RANGE}
            testId="settings-menu-pi-chat-input-font-size-ticks"
          />
          <div className="text-xs text-vibe-text-secondary mt-1">
            The message box you type in AND the streaming status indicator
            ({PI_CHAT_INPUT_FONT_SIZE_RANGE.min}–{PI_CHAT_INPUT_FONT_SIZE_RANGE.max}px), independent of the
            transcript size. Per tenant per browser. Default {PI_CHAT_INPUT_FONT_SIZE_RANGE.default}px.
          </div>
        </div>
        </div>
        </Section>

        <Section title="General" testId="settings-menu-section-general">
        <div className="space-y-3">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="auto-rebuild-checkbox"
            checked={autoRebuildOnSave === true}
            disabled={isLoadingConfig}
            onChange={handleToggle}
            className="mt-1"
          />
          <div className="flex-1">
            <div className="text-sm text-vibe-text">Auto-rebuild on save</div>
            <div className="text-xs text-vibe-text-secondary mt-0.5">
              When enabled, saving a file triggers a debounced Vite rebuild
              and refreshes the preview iframe automatically. Persists per
              tenant across backend restart.
            </div>
          </div>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="agent-mm-post-checkbox"
            checked={agentMmPostEnabled === true}
            disabled={agentMmPostEnabled === null}
            onChange={handleMmPostToggle}
            className="mt-1"
          />
          <div className="flex-1">
            <div className="text-sm text-vibe-text">Agents may post to Mattermost</div>
            <div className="text-xs text-vibe-text-secondary mt-0.5">
              Allow AI agents (Hermes, the desktop app, and other tenant
              agents) to post messages to your Mattermost channels. Posts are
              always attributed to the agent, never the bare bot. Off by
              default; applies immediately and persists per tenant.
            </div>
          </div>
        </label>
        </div>
        </Section>

        {/* Tenant-keys arc (2026-07-13): cloud provider API keys — stored
            server-side, never echoed; the router's no-key 503 points here.
            ProviderKeysSection renders its own heading, so this card is
            title-less. */}
        <Section testId="settings-menu-section-provider-keys">
          <ProviderKeysSection />
        </Section>

        <Section title="Voice" testId="settings-menu-section-voice">
        <div className="space-y-3">
          {engines.length > 0 && (
            <div>
              <label className="block text-sm text-vibe-text mb-1.5">TTS engine</label>
              <select
                value={voiceSettings.engine}
                onChange={(e) => void setEngine(e.target.value)}
                data-testid="settings-menu-engine"
                className="block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text text-sm"
              >
                {engines.map((eng) => (
                  <option key={eng.id} value={eng.id}>
                    {eng.label}
                  </option>
                ))}
              </select>
              <div className="text-xs text-vibe-text-secondary mt-1">
                Switching the engine reloads its voice list and resets to that engine's default voice.
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm text-vibe-text mb-1.5">Voice</label>
            <select
              value={voiceSettings.voice}
              onChange={(e) => updateVoiceSettings({ voice: e.target.value as VoicePresetId })}
              data-testid="settings-menu-voice"
              className="block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text text-sm"
            >
              {voices.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.id}{v.gender ? ` (${v.gender})` : ''}{v.description ? ` — ${v.description}` : ''}
                </option>
              ))}
            </select>
            <div className="text-xs text-vibe-text-secondary mt-1">
              TTS voice for the assistant. Persists per browser via localStorage.
            </div>
          </div>
        </div>
        </Section>

        <Section title="Pi chat" testId="settings-menu-section-pi-chat">
        <div className="space-y-3">
        <div>
          <label className="block text-sm text-vibe-text mb-1.5">Chat harness</label>
          <select
            value={harness}
            onChange={(e) => {
              const next = e.target.value as ChatHarnessId;
              if (!tenantId) {
                toast.error('Tenant not loaded yet — try again in a moment.', 3000);
                return;
              }
              setHarness(tenantId, next);
              toast.success(`Chat harness: ${next}`, 2000);
            }}
            data-testid="settings-menu-chat-harness"
            disabled={!tenantId}
            className="block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text text-sm disabled:opacity-50"
          >
            <option value="legacy">{CHAT_HARNESS_LABELS.legacy}</option>
            <option value="pi">{CHAT_HARNESS_LABELS.pi}</option>
            <option value="none">{CHAT_HARNESS_LABELS.none}</option>
          </select>
          <div className="text-xs text-vibe-text-secondary mt-1">
            Selects which chat panel the right pane mounts. Persists per
            tenant per browser via localStorage. Default is Legacy.
          </div>
        </div>

        <div>
          <label className="block text-sm text-vibe-text mb-1.5">
            Pi chat thinking
          </label>
          <div
            role="radiogroup"
            aria-label="Pi chat thinking level"
            className="flex gap-1"
            data-testid="settings-menu-pi-chat-thinking"
          >
            {(['off', 'low', 'medium', 'high'] as const).map((lvl) => {
              const active = thinkingLevel === lvl;
              return (
                <button
                  key={lvl}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-testid={`settings-menu-pi-chat-thinking-${lvl}`}
                  disabled={!tenantId}
                  onClick={() => {
                    if (!tenantId) return;
                    setThinkingLevel(tenantId, lvl as ThinkingLevel);
                    toast.success(`Pi thinking: ${lvl}`, 1500);
                  }}
                  className={`flex-1 px-2 py-1 text-xs rounded border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    active
                      ? 'bg-vibe-accent/20 border-vibe-accent/40 text-vibe-text'
                      : 'border-vibe-border text-vibe-text-secondary hover:bg-vibe-bg-tertiary'
                  }`}
                >
                  {lvl}
                </button>
              );
            })}
          </div>
          <div className="text-xs text-vibe-text-secondary mt-1">
            Show the LLM&apos;s reasoning steps (collapsible) before its final
            answer. Higher levels = more detailed thinking but more tokens
            consumed. Applies to NEW Pi chat tabs created after the change;
            existing tabs keep their setting until you change it inline.
            Default off. Local Qwen3.6 model verified to emit streaming
            reasoning chunks (2026-05-10).
          </div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="settings-menu-pi-chat-auto-compact"
            checked={autoCompact}
            disabled={!tenantId}
            onChange={() => {
              if (!tenantId) return;
              const next = !autoCompact;
              setAutoCompact(tenantId, next);
              toast.success(
                next ? 'Pi auto-compact enabled' : 'Pi auto-compact disabled',
                2000,
              );
            }}
            className="mt-1"
          />
          <div className="flex-1">
            <div className="text-sm text-vibe-text">Pi chat auto-compact</div>
            <div className="text-xs text-vibe-text-secondary mt-0.5">
              When the conversation crosses the context WARNING threshold
              (80%), automatically summarize it in place instead of waiting
              for you to click Compact. You can also compact manually any
              time with <span className="font-mono">/compact</span>. Persists
              per tenant per browser.
            </div>
          </div>
        </label>

        <div>
          <label className="block text-sm text-vibe-text mb-1.5">
            Pi chat queue mode
          </label>
          <div
            role="radiogroup"
            aria-label="Pi chat queue mode"
            className="flex gap-1"
            data-testid="settings-menu-pi-chat-queue-mode"
          >
            {(['followUp', 'steer'] as const).map((mode) => {
              const active = queueMode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-testid={`settings-menu-pi-chat-queue-mode-${mode}`}
                  disabled={!tenantId}
                  onClick={() => {
                    if (!tenantId) return;
                    setQueueMode(tenantId, mode as QueueMode);
                    toast.success(`Pi queue mode: ${mode}`, 1500);
                  }}
                  className={`flex-1 px-2 py-1 text-xs rounded border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    active
                      ? 'bg-vibe-accent/20 border-vibe-accent/40 text-vibe-text'
                      : 'border-vibe-border text-vibe-text-secondary hover:bg-vibe-bg-tertiary'
                  }`}
                >
                  {mode}
                </button>
              );
            })}
          </div>
          <div className="text-xs text-vibe-text-secondary mt-1">
            When the Pi chat is mid-stream and you submit a new message from
            the queue input row: <strong>followUp</strong> queues it for after
            the agent finishes its current task (default; matches Claude
            Code). <strong>steer</strong> injects it between assistant turns
            of the current run, modifying the trajectory mid-task. Persists
            per tenant per browser.
          </div>
        </div>
        </div>
        </Section>

        {/* 1.554.0 — Pi status spinner: pick the animated "thinking" theme, its
            rotating message pool, and a size multiplier (1× = original spinner
            size). Default cosmos/cosmos/1× is the original Cosmos theme + phrases
            at the original size, so picking a different theme or size is opt-in. */}
        <Section title="Pi status spinner" testId="settings-menu-section-spinner">
        <div className="space-y-3">
        <div>
          <label className="block text-sm text-vibe-text mb-1.5">Style</label>
          <select
            value={spinnerVariant}
            onChange={(e) => {
              if (!tenantId) {
                toast.error('Tenant not loaded yet — try again in a moment.', 3000);
                return;
              }
              const next = e.target.value as SpinnerSelectable;
              setSpinnerVariant(tenantId, next);
              toast.success(`Spinner: ${spinnerOptionLabel(next)}`, 1500);
            }}
            data-testid="settings-menu-spinner-variant"
            disabled={!tenantId}
            className="block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text text-sm disabled:opacity-50"
          >
            {SPINNER_SELECTABLE.map((v) => (
              <option key={v} value={v}>
                {spinnerOptionLabel(v)}
              </option>
            ))}
          </select>
          <div className="text-xs text-vibe-text-secondary mt-1">
            The animation shown while Pi is thinking. <strong>Rotate</strong>{' '}
            cycles through every theme. Per tenant per browser. Default Cosmos.
          </div>
        </div>

        <div>
          <label className="block text-sm text-vibe-text mb-1.5">Messages</label>
          <select
            value={spinnerMessages}
            onChange={(e) => {
              if (!tenantId) {
                toast.error('Tenant not loaded yet — try again in a moment.', 3000);
                return;
              }
              const next = e.target.value as SpinnerSelectable;
              setSpinnerMessages(tenantId, next);
              toast.success(`Spinner messages: ${spinnerOptionLabel(next)}`, 1500);
            }}
            data-testid="settings-menu-spinner-messages"
            disabled={!tenantId}
            className="block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text text-sm disabled:opacity-50"
          >
            {SPINNER_SELECTABLE.map((v) => (
              <option key={v} value={v}>
                {spinnerOptionLabel(v)}
              </option>
            ))}
          </select>
          <div className="text-xs text-vibe-text-secondary mt-1">
            Which theme&apos;s rotating status phrases to show — independent of
            the style above. <strong>Rotate</strong> draws from every pool.
          </div>
        </div>

        <div>
          <label className="block text-sm text-vibe-text mb-1.5">Message font</label>
          <select
            value={spinnerFont}
            onChange={(e) => {
              if (!tenantId) {
                toast.error('Tenant not loaded yet — try again in a moment.', 3000);
                return;
              }
              const next = e.target.value as SpinnerFontId;
              setSpinnerFont(tenantId, next);
              toast.success(
                `Spinner font: ${SPINNER_FONTS.find((f) => f.id === next)?.name ?? next}`,
                1500,
              );
            }}
            data-testid="settings-menu-spinner-font"
            disabled={!tenantId}
            className="block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text text-sm disabled:opacity-50"
          >
            {SPINNER_FONTS.map((f) => (
              <option key={f.id} value={f.id} style={{ fontFamily: f.family }}>
                {f.name}
              </option>
            ))}
          </select>
          <div className="text-xs text-vibe-text-secondary mt-1">
            Font for the &ldquo;thinking&rdquo; status phrase. <strong>Matrix</strong> decodes
            green katakana into the phrase; <strong>Kanji</strong> shows a Japanese phrase
            with an English subtitle. Per tenant per browser. Default matches the app font.
          </div>
        </div>

        <div>
          <label className="block text-sm text-vibe-text mb-1.5">
            Size: <span className="font-mono">{spinnerScale}×</span>
          </label>
          <input
            type="range"
            min={SPINNER_SCALE_RANGE.min}
            max={SPINNER_SCALE_RANGE.max}
            step={0.25}
            value={spinnerScale}
            onChange={(e) => {
              if (!tenantId) return;
              setSpinnerScale(tenantId, Number(e.target.value));
            }}
            data-testid="settings-menu-spinner-scale"
            disabled={!tenantId}
            className="block w-full disabled:opacity-50"
          />
          <div
            data-testid="settings-menu-spinner-scale-ticks"
            aria-hidden="true"
            className="flex justify-between text-[10px] leading-none font-mono text-vibe-text-secondary mt-1 px-0.5"
          >
            <span>{SPINNER_SCALE_RANGE.min}×</span>
            <span>{SPINNER_SCALE_RANGE.default}× (default)</span>
            <span>{SPINNER_SCALE_RANGE.max}×</span>
          </div>
          <div className="text-xs text-vibe-text-secondary mt-1">
            Scales the animation and its status message ({SPINNER_SCALE_RANGE.min}×–{SPINNER_SCALE_RANGE.max}×).
            {' '}{SPINNER_SCALE_RANGE.min}× is the original spinner size; {SPINNER_SCALE_RANGE.max}× is triple.
            Per tenant per browser. Default {SPINNER_SCALE_RANGE.default}×.
          </div>
        </div>

        <div>
          <label className="block text-sm text-vibe-text mb-1.5">In-thread animation</label>
          <select
            value={asciiSpinner}
            onChange={(e) => {
              if (!tenantId) {
                toast.error('Tenant not loaded yet — try again in a moment.', 3000);
                return;
              }
              const next = e.target.value as AsciiSpinnerSelectable;
              setAsciiSpinner(tenantId, next);
              toast.success(`In-thread animation: ${asciiSpinnerOptionLabel(next)}`, 1500);
            }}
            data-testid="settings-menu-ascii-spinner"
            disabled={!tenantId}
            className="block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text text-sm disabled:opacity-50"
          >
            {ASCII_SPINNER_SELECTABLE.map((v) => (
              <option key={v} value={v}>
                {asciiSpinnerOptionLabel(v)}
              </option>
            ))}
          </select>
          <div className="text-xs text-vibe-text-secondary mt-1">
            The small Braille dot-canvas shown IN the chat thread where Pi&apos;s next
            message appears (distinct from the status spinner above). <strong>Rotate</strong>{' '}
            cycles the animations per turn. Per tenant per browser. Default Rotate.
          </div>
        </div>
        </div>
        </Section>

        {/* Phase 4 (2026-07-13): push browser-held chat/voice history to the
            knowledge backend → MIND `sessions`. Idempotent (content-hash
            dedup server-side) — safe to click repeatedly. */}
        <Section title="Chat history" testId="settings-menu-section-chat-history">
          <label className="block text-sm text-vibe-text mb-1.5">
            Chat history → knowledge
          </label>
          <button
            type="button"
            data-testid="settings-menu-sync-history"
            disabled={!tenantId || historySyncing}
            onClick={() => {
              if (!tenantId) return;
              setHistorySyncing(true);
              syncHistoryToKnowledge(tenantId)
                .then((summary) => {
                  if (!summary) {
                    toast.success('No chat history in this browser yet', 2500);
                    return;
                  }
                  toast.success(
                    `History synced: ${summary.new} new, ` +
                      `${summary.duplicates} already known, ` +
                      `${summary.ingested} ingested to MIND`,
                    4000,
                  );
                })
                .catch((err: unknown) => {
                  toast.error(
                    `History sync failed: ${err instanceof Error ? err.message : String(err)}`,
                  );
                })
                .finally(() => setHistorySyncing(false));
            }}
            className="w-full px-2 py-1 text-xs rounded border border-vibe-border text-vibe-text-secondary hover:bg-vibe-bg-tertiary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {historySyncing ? 'Syncing…' : 'Sync history to knowledge'}
          </button>
          <div className="text-xs text-vibe-text-secondary mt-1">
            Pushes this browser&apos;s Pi chat sessions and voice history to
            the tenant knowledge base (MIND sessions collection) so agents can
            recall past conversations. Already-synced sessions are skipped.
          </div>
        </Section>
      </div>
    </div>
    </>
  );
}

export default SettingsMenu;
